import api from './client';
import type { Appointment } from 'types/garage/planning';

interface ApiAppointment {
  id: string;
  org_id: string;
  client_name: string;
  plate: string;
  category: string;
  date: string;
  start_hour: number;
  duration_hours: number;
  all_day?: boolean;
  end_date?: string;
  has_car_icon: boolean;
  has_mail_icon: boolean;
  ordre_id?: string;
  dossier_id?: string;
  reservation_id?: string;
  type?: string;
  assigned_to?: string;
  status?: string;
  num?: string;
  has_loaner?: boolean;
}

function toFrontend(a: ApiAppointment): Appointment {
  return {
    id: a.id,
    clientName: a.client_name,
    plate: a.plate,
    category: a.category as Appointment['category'],
    date: a.date,
    startHour: a.start_hour,
    durationHours: a.duration_hours,
    allDay: a.all_day ?? false,
    endDate: a.end_date,
    hasCarIcon: a.has_car_icon,
    hasMailIcon: a.has_mail_icon,
    ordre_id: a.ordre_id,
    dossier_id: a.dossier_id,
    reservation_id: a.reservation_id,
    org_id: a.org_id,
    type: a.type as Appointment['type'],
    assigned_to: a.assigned_to,
    status: a.status as Appointment['status'],
    num: a.num,
    hasLoaner: a.has_loaner,
  };
}

function toApi(a: Partial<Appointment> & { org_id?: string }): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (a.clientName !== undefined) out.client_name = a.clientName;
  if (a.plate !== undefined) out.plate = a.plate;
  if (a.category !== undefined) out.category = a.category;
  if (a.date !== undefined) out.date = a.date;
  if (a.startHour !== undefined) out.start_hour = a.startHour;
  if (a.durationHours !== undefined) out.duration_hours = a.durationHours;
  if (a.allDay !== undefined) out.all_day = a.allDay;
  if (a.endDate !== undefined) out.end_date = a.endDate;
  if (a.hasCarIcon !== undefined) out.has_car_icon = a.hasCarIcon;
  if (a.hasMailIcon !== undefined) out.has_mail_icon = a.hasMailIcon;
  if (a.ordre_id !== undefined) out.ordre_id = a.ordre_id;
  if (a.dossier_id !== undefined) out.dossier_id = a.dossier_id;
  if (a.reservation_id !== undefined) out.reservation_id = a.reservation_id;
  if (a.org_id !== undefined) out.org_id = a.org_id;
  if (a.type !== undefined) out.type = a.type;
  if (a.assigned_to !== undefined) out.assigned_to = a.assigned_to;
  return out;
}

export const appointmentsApi = {
  list: async (params?: { org_id?: string; date?: string; date_from?: string; date_to?: string; category?: string; limit?: number; offset?: number }): Promise<Appointment[]> => {
    const { data } = await api.get('/appointments', { params });
    return (data as ApiAppointment[]).map(toFrontend);
  },

  create: async (appointment: Omit<Appointment, 'id'> & { org_id: string }): Promise<Appointment> => {
    const { data } = await api.post('/appointments', toApi(appointment));
    return toFrontend(data);
  },

  update: async (id: string, updates: Partial<Appointment>): Promise<Appointment> => {
    const { data } = await api.patch(`/appointments/${id}`, toApi(updates));
    return toFrontend(data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/appointments/${id}`);
  },

  /**
   * Predict a ServiceBox "créneau refusé": does the proposed reception/restitution
   * slot collide with another RDV on the same reception column? Returns, per event,
   * the conflict + free reception columns of the same kind to switch to.
   */
  checkReceptionConflicts: async (payload: {
    ordre_id: string;
    reception_date?: string;
    reception_time?: string;
    restitution_date?: string;
    restitution_time?: string;
    org_id?: string;
  }): Promise<ReceptionConflictResult> => {
    const { data } = await api.post('/appointments/check-reception-conflicts', payload);
    return data as ReceptionConflictResult;
  },

  /** Occupied 7.5-min slots (HH:MM) on the ordre's reception column for a date — to grey them out. */
  occupiedSlots: async (params: { ordre_id: string; type: "reception" | "restitution"; date: string; org_id?: string }): Promise<string[]> => {
    const { data } = await api.get('/appointments/occupied-slots', { params });
    return (data as { slots: string[] }).slots || [];
  },
};

export interface ReceptionConflictEvent {
  type: "reception" | "restitution";
  category: string;
  category_label: string;
  appointment_id: string;
  conflict: boolean;
  free_receptions: { id: string; label: string }[];
  conflict_with?: { client_name: string; plate: string; time: string };
}

export interface ReceptionConflictResult {
  reception: ReceptionConflictEvent | null;
  restitution: ReceptionConflictEvent | null;
}
